import { say } from '@/lib/vocabulary';
import { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { backend } from '@/api/backend';
import { explainLine } from '@/api/explain';
import { ApiError } from '@/api/http';
import { EmptyState, Panel, PanelHeader, Loading } from '@/components/ui';
import { ToolIcon } from '@/components/ToolIcon';
import { WalkProgress } from '@/components/WalkProgress';
import { every, reconstructionOutstanding } from '@/pages/livePolling';

const retry = (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2);

/** The bucket record, and the walkthrough read of it, exactly as the API types them. */
type Registered = Awaited<ReturnType<typeof backend.getBucket>>;
type Shell = { data?: Awaited<ReturnType<typeof backend.getWalkthrough>>; isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown };

/**
 * Every section repeating the same sentence about why it cannot be opened
 * filled the page with twenty identical paragraphs and still never said what
 * to do. When nothing can serve rows, the reason is stated once with the two
 * surfaces that DO work; each section then reads as a chip carrying its own
 * state, and its reason on hover.
 */


/** The distinct reasons the backend gave for the sections it cannot open. */
function reasonsFor(sections: { availability: string; reason?: string | null }[]): string[] {
  return [...new Set(sections.filter((s) => s.availability === 'UNSUPPORTED' && s.reason).map((s) => s.reason!))];
}

type Section = { availability: string; reason?: string | null };

/**
 * What this whole bucket amounts to, said once at the top.
 *
 * There was a banner for this and it required two things at once - everything
 * blocked AND everything empty - so it fired only for a bucket that had been
 * read everywhere and held nothing. The case that actually happens is the
 * other one: every section UNSUPPORTED because reconstruction has published
 * nothing yet. On capway that is 29 sections across 18 tools, all of them, and
 * the banner stayed silent while each tool repeated the same two sentences
 * underneath. Eighteen copies of one fact is not a page, and a reader is left
 * to work out from a wall of identical chips whether "cannot open" means their
 * corpus is empty or the software is not built.
 *
 * So the aggregate is stated whenever nothing can be opened, and it says which
 * of the two answers it is, with each distinct reason given once and counted.
 */
export function summarise(tools: { sections: Section[] }[]): { headline: string; reasons: { text: string; n: number }[] } | null {
  const all = tools.flatMap((t) => t.sections);
  if (tools.length === 0) return null;
  if (all.some((s) => s.availability === 'SUPPORTED')) return null;

  const empty = all.filter((s) => s.availability === 'EMPTY').length;
  const blocked = all.filter((s) => s.availability === 'UNSUPPORTED');

  const counts = new Map<string, number>();
  for (const s of blocked) if (s.reason) counts.set(s.reason, (counts.get(s.reason) ?? 0) + 1);
  const reasons = [...counts].map(([text, n]) => ({ text, n })).sort((a, b) => b.n - a.n);

  if (all.length === 0)
    return { headline: `No section was reported for any of these ${tools.length} tools.`, reasons: [] };
  if (blocked.length === 0)
    return { headline: `Every one of these ${empty} sections was read, and every one holds nothing.`, reasons: [] };
  if (empty === 0)
    return {
      headline: `Nothing here can be opened yet: all ${blocked.length} sections across ${tools.length} tools, for the reasons below. This is about what has been built, not about what this bucket holds.`,
      reasons,
    };
  return {
    headline: `Nothing here can be opened yet. ${empty} of these ${all.length} sections were read and hold nothing; the other ${blocked.length} could not be opened, for the reasons below.`,
    reasons,
  };
}

/**
 * A section is in one of three states and the page drew two of them.
 *
 * SUPPORTED holds records. EMPTY was looked at and holds none, a counted
 * zero. UNSUPPORTED could not be looked at, and carries the backend's reason
 * for that. The last two were the same grey chip, separated only by a hover
 * title, so "this bucket has no channels" and "the product cannot open
 * channels" read identically. They are opposite answers to the only question
 * a buyer is asking, and they are about to appear side by side: a producer
 * that fills some sections and not others turns this from cosmetic into a
 * misreport.
 */
const SECTION_STATE: Record<string, { note: string; className: string; title: string }> = {
  SUPPORTED: {
    note: '',
    className: 'border border-ok/40 bg-ok-soft text-ok',
    title: 'This section holds records.',
  },
  EMPTY: {
    note: 'none',
    className: 'border border-line bg-panel-2 text-ink-2',
    title: 'This section was read and holds nothing. The count is a real zero.',
  },
  UNSUPPORTED: {
    note: 'cannot open',
    // ink-4 on panel-2 measures 2.94:1 in both themes, against a 4.5:1 floor
    // at this size, so the chip carrying the most consequential message on
    // the page was the least readable thing on it.
    className: 'border border-dashed border-line-2 bg-panel-2 text-ink-2',
    title: 'This section cannot be opened yet.',
  },
  /**
   * A state this build has not been taught.
   *
   * It used to fall through to UNSUPPORTED, which converts an unknown into
   * one of the two definite answers a buyer is asking about.
   */
  UNRECOGNISED: {
    note: 'state not recognised here',
    className: 'border border-dashed border-warn bg-panel-2 text-warn',
    title: 'This build does not recognise the state this section reported.',
  },
};

/**
 * One section, as a chip that states which of the three answers it is.
 *
 * Exported so its RENDERED output can be asserted. Its predecessor was
 * guarded by a test that read this file as a string, which passed while the
 * word "none" existed in the source and never reached a single DOM node,
 * because the state was tied to the count and EMPTY always carries a counted
 * zero.
 */
export function SectionChip({
  section,
}: {
  section: { key: string; label: string; availability: string; reason?: string | null; recordCount?: number | null };
}) {
  const state = SECTION_STATE[section.availability] ?? SECTION_STATE.UNRECOGNISED!;
  const count = typeof section.recordCount === 'number' ? section.recordCount.toLocaleString() : null;
  return (
    <span className={`rounded px-1.5 py-0.5 text-caption ${state.className}`}>
      {section.label}
      {state.note ? ` · ${state.note}` : ''}
      {count === null ? '' : <span className="mono"> · {count}</span>}
    </span>
  );
}

/**
 * One bucket's records, as a panel.
 *
 * This was written inline inside the company page's map, which made the
 * company page the only way to read it: browsing the records of the bucket you
 * were working in meant opening a page that drew every OTHER bucket the
 * company owns, above and below yours. The panel is the unit, and the unit is
 * one bucket, so it is a component that either page can render.
 */
function ScopeRecords({
  b,
  q,
  onSealed,
  named,
}: {
  b: Registered;
  q: Shell | undefined;
  onSealed: () => void;
  /** The company page stacks several, so each must say which it is. */
  named?: boolean;
}) {
  return (
            <Panel>
              {named && (
                <PanelHeader title={b.bucket.name} sub={`${b.bucket.region} · ${say(b.bucket.inventoryCoverage)}`} />
              )}
              {/* On a sealed scope there is no walk to watch, and the strip
                  sat at the top of every one of them reading CONNECTING. */}
              {!q?.data?.sealedAt && <WalkProgress bucketId={b.bucket.id} onSealed={onSealed} compact />}
              {q?.isPending ? (
                <p className="px-4 py-3 text-small text-ink-3">Reading this bucket.</p>
              ) : q?.isError ? (
                // A failed read was quiet grey prose with no tone, no status
                // role and no way to ask again, so it read as a note rather
                // than a failure.
                <p role="alert" className="flex flex-wrap items-center gap-2 px-4 py-3 text-small text-warn">
                  <span className="min-w-0 flex-1">{explainLine(q.error, 'read this bucket')}</span>
                  <button
                    type="button"
                    onClick={() => void q.refetch()}
                    className="flex-none rounded border border-line bg-panel px-2.5 py-1 text-acc-2 transition-colors duration-fast hover:bg-hov"
                  >
                    Try again
                  </button>
                </p>
              ) : (
                <>
                  {q!.data!.tools.length === 0 ? (
                    <div className="flex flex-wrap items-center gap-3 px-4 py-3 text-small text-ink-3">
                      <span className="min-w-0 flex-1">
                        No tools found here yet. The strip above shows this scope&rsquo;s walk; tools appear as it seals
                        them.
                      </span>
                      <Link
                        to={`/buckets/${b.bucket.id}`}
                        className="rounded border border-line bg-panel-2 px-2.5 py-1 text-acc-2 transition-colors duration-fast hover:bg-hov"
                      >
                        Open its pipeline
                      </Link>
                    </div>
                  ) : (
                    <>
                      {(() => {
                        const sum = summarise(q!.data!.tools);
                        if (!sum) return null;
                        return (
                          <div className="border-b border-line bg-panel-2 px-4 py-3">
                            <p className="text-small text-ink">{sum.headline}</p>
                            {sum.reasons.map((r) => (
                              <p key={r.text} className="mt-1.5 flex gap-2 text-caption text-ink-2">
                                <span className="mono flex-none text-ink-4">{r.n}×</span>
                                <span className="min-w-0">{r.text}</span>
                              </p>
                            ))}
                          </div>
                        );
                      })()}
                      {q!.data!.tools.map((tool) => (
                        <div key={tool.toolId} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line px-4 py-2.5 last:border-0">
                          <ToolIcon tool={tool.toolId} display={tool.display} size={16} />
                          <span className="text-row font-medium text-ink">{tool.display.label}</span>
                          {tool.family && (
                            <span className="text-caption text-ink-4">{say(tool.family)}</span>
                          )}
                          <span className="flex flex-wrap items-center gap-1.5">
                            {tool.sections.map((sec) => (
                              <SectionChip key={sec.key} section={sec} />
                            ))}
                          </span>
                          {/* A tool that came back with no sections at all
                              rendered as a bare name and nothing else, which
                              reads as a row that failed to load rather than
                              one the backend answered. */}
                          {tool.sections.length === 0 && (
                            <span className="text-caption text-ink-3">
                              this tool published no sections for this scope
                            </span>
                          )}
                          {/* The reason used to live in a title attribute,
                              which is unreachable on touch, unreachable by
                              keyboard, and dropped by screen readers whenever
                              the element has its own text. It is content now.
                              One line per distinct reason, because fifteen
                              sections usually share one. */}
                          {reasonsFor(tool.sections).map((reason) => (
                            <span key={reason} className="w-full text-caption text-ink-3">
                              {say(reason)}
                            </span>
                          ))}
                        </div>
                      ))}
                    </>
                  )}
                  <p className="px-4 py-2.5 text-caption text-ink-4">
                    {q!.data!.sealedAt
                      ? `Sealed ${new Date(q!.data!.sealedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}.`
                      : 'Sections fill in once records are published. Nothing here is invented to fill a gap.'}
                  </p>
                </>
              )}
            </Panel>
  );
}

/**
 * The records of the bucket you are working in, and no other.
 *
 * The bucket page offered "Walkthrough", and it led to the company page above:
 * a stack of panels, one per bucket the company owns, with the one you came
 * from somewhere in the middle. Reading your own corpus meant scrolling past
 * everyone else's. This reads the same per-bucket endpoint the company page
 * fans out over, once, for the bucket in the URL.
 */
export function BucketWalkthroughLive() {
  const { bucketId } = useParams();
  const qc = useQueryClient();
  const bucket = useQuery({
    queryKey: ['flow-bucket', bucketId],
    queryFn: ({ signal }) => backend.getBucket(bucketId!, signal),
    enabled: Boolean(bucketId),
    retry,
  });
  /**
   * What reconstruction has been asked for here, and whether any of it is
   * still coming.
   *
   * This page is where a reader waits for records, and records arrive without
   * anyone pressing anything: promotion enqueues a reconstruction and a sweep
   * drains the queue every five minutes. So the walk sealing is not on its own
   * a reason to stop reading - but an unconditional twelve-second poll, which
   * is what this had, keeps asking a sealed bucket with nothing outstanding
   * for as long as the tab is open.
   */
  const requests = useQuery({
    queryKey: ['reconstruction-requests', bucketId],
    queryFn: ({ signal }) => backend.listReconstructionRequests(bucketId!, signal),
    enabled: Boolean(bucketId),
    retry,
    refetchInterval: (query) => every(10_000, reconstructionOutstanding(query.state.data?.items)),
  });
  const coming = reconstructionOutstanding(requests.data?.items);
  const shell = useQuery({
    queryKey: ['walkthrough', bucketId],
    queryFn: ({ signal }) => backend.getWalkthrough(bucketId!, signal),
    enabled: Boolean(bucketId),
    retry,
    // Two reasons to keep reading and no third: the walk has not sealed, or a
    // reconstruction is still to land. Neither is true on a settled bucket.
    refetchInterval: (query) => every(12_000, !query.state.data?.sealedAt || coming),
  });
  const onSealed = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['walkthrough', bucketId] });
  }, [qc, bucketId]);

  if (bucket.isPending) return <Loading what="Reading this bucket" rows={5} />;

  if (bucket.isError || !bucket.data)
    return <EmptyState title="This bucket is unavailable" description={explainLine(bucket.error, 'read this bucket')} />;

  return (
    <div className="min-h-0 flex-1 animate-pop space-y-3 overflow-y-auto p-5">
      <h1 className="text-h2 text-ink">Browse the records</h1>
      <p className="max-w-4xl text-small text-ink-2">
        Sections open <span className="mono text-ink">{bucket.data.bucket.name}</span> record by record. A section
        marked <span className="text-ink">none</span> was read and holds nothing; one marked{' '}
        <span className="text-ink">cannot open</span> is waiting on the software that reads that kind of record, and on
        the records themselves, neither of which exists yet. Packaging this bucket and opening its graph both work
        meanwhile.
      </p>
      {/* Said rather than only acted on. The page keeps reading while this is
          true, and a reader watching an empty section deserves to know the
          difference between "nothing was found" and "it has not run yet". */}
      {coming && (
        <p className="text-small text-acc-2">
          A reconstruction is queued or running on this bucket. Sections fill as it publishes; this page is watching.
        </p>
      )}
      <ScopeRecords b={bucket.data} q={shell} onSealed={onSealed} />
    </div>
  );
}
